import isAuthenticated from './applications/isAuthenticated.js'
import LocalRepo from './infrastructure/localRepo.js'
import NetworkRepo from './infrastructure/web-backend/networkRepo.js'
import initListeners from './listeners/init.js'
import Events from './utils/events.js'
import Translator from './utils/translator.js'
import makeSignup from './applications/makeSignup.js'
import makeLogin from './applications/makeLogin.js'
import Storage from '@/shared/utils/storage.js'
import { notificationService } from '../services/notificationService.js'

class AuthService {
  static _instance = null

  constructor({ Events, sendBugAlert, configs, translate } = {}) {
    if (AuthService._instance) return AuthService._instance

    if (!Events || !sendBugAlert) {
      throw new Error('Events et sendBugAlert sont requis pour AuthService')
    }

    this.events = Events
    this.sendBugAlert = sendBugAlert
    this.translate =
      typeof translate == 'function'
        ? (key, ...args) => translate(`auth.${key}`, ...args)
        : Translator.translate
    this.configs = configs || {}
    this.localRepo = LocalRepo.init()
    this.networkRepo = NetworkRepo.init()
    this.setupFallbacks()

    initListeners(this)
    AuthService._instance = this
  }

  setupFallbacks() {
    if (
      typeof this.events?.dispatch !== 'function' ||
      typeof this.events?.handle !== 'function'
    ) {
      console.warn('Events instance is not valid, creating internal event.')
      this.events = new Events()
    }

    if (typeof this.sendBugAlert !== 'function') {
      this.sendBugAlert = (...args) => {
        console.error('sendBugAlert not provided', ...args)
      }
    }

    if (typeof this.translate !== 'function') {
      this.translate = (...args) => Translator.translate(...args)
    }
  }

  static get events() {
    return this.getInstance()?.events
  }

  static get configs() {
    return this.getInstance()?.configs
  }

  static get translate() {
    return this.getInstance()?.translate
  }

  static get translations() {
    return Translator.translations
  }

  static getInstance() {
    if (!AuthService._instance) {
      throw new Error(
        'Instance de AuthService non créée, appelle le constructeur d’abord.',
      )
    }
    return AuthService._instance
  }

  static async isAuthenticated() {
    const instance = AuthService.getInstance()
    const isAuth = await isAuthenticated(instance)
    if (isAuth) {
      const userData = await instance.localRepo.get()
      console.log("🚀 ~ AuthService ~ isAuthenticated ~ userData:", userData)
      if (userData?.id) {
        AuthService.syncDeviceToken(userData.id)
      }
      if (userData?.metadata?.riderId) {
        AuthService.syncDeviceToken(userData.metadata.riderId)
      }
      
    }
    return isAuth
  }

  static async signup(payload) {
    const instance = AuthService.getInstance()
    let res
    try {
      res = await makeSignup(instance, payload)
      if (res.success && res.user?.id) {
        AuthService.syncDeviceToken(res.user.id)
      }
    } catch (error) {
      console.log('🚀 ~ AuthService ~ signup ~ error:', error)
      instance.sendBugAlert(error)
      res = error
    }
    return res
  }

  static async login(payload) {
    const instance = AuthService.getInstance()
    let res
    try {
      res = await makeLogin(instance, payload)
      if (res.success && res.user?.id) {
        AuthService.syncDeviceToken(res.user.id)
      }
    } catch (error) {
      console.log('🚀 ~ AuthService ~ login ~ error:', error)
      instance.sendBugAlert(error)
      res = error
    }
    return res
  }

  static getUserData(getReactive = false) {
    let data = Storage.get('userData')
    if (getReactive) data = Storage.useValue('userData')
    return data
  }

  static async syncDeviceToken(userId) {
    console.log('🚀 ~ syncDeviceToken ~ userId:', userId)
    if (!userId) return
    try {
      let token = await notificationService.getDeviceToken()

      if (token) {
        console.log('[Auth Service] Got device token, syncing:', token)
        await notificationService.uploadDeviceToken(userId, token)
        
        // Update user metadata in Storage if needed
        const instance = AuthService.getInstance()
        const userData = await instance.localRepo.get()
        
        if (userData && (!userData.metadata || userData.metadata.deviceToken !== token)) {
          const updatedUser = {
            ...userData,
            metadata: {
              ...(userData.metadata || {}),
              deviceToken: token
            }
          }
          await instance.localRepo.save(updatedUser)
        }
      } else {
        console.log('[Auth] No device token found (browser or permission denied)')
      }
    } catch (error) {
      console.error('[Auth Service] Error syncing device token:', error)
    }
  }
}

export default AuthService
